import { Tabs, TabsContent, TabsList, TabsTrigger } from "@component/tabs";
import { BuildingIcon, MailIcon, UserIcon, UsersIcon } from "lucide-react";
import { useUserProfileContext } from "@context/user_profile";
import { useEffect, useState } from "react";
import { getCompanyInfo } from "@service/user";
import { Company } from "@type/company";
import SettingProfileFormV2 from "./setting-profile-form-v2";
import SettingUserPermissions from "./setting-user-permission";
import CompanyInformationSectionV2 from "./setting-company-information-v2";
import EmailCommunicationSection from "./setting-email-communication";
import EmailCommunicationTrainerSection from "./setting-email-communication-trainer";
import { useOuraWorkspace } from "@oura/role";
import { useMaintenanceGate } from "@context/maintenance";
import { PageHead, SURFACE } from "@oura/ui";
import { cn } from "@oura/lib/cn";

export default function SettingV2() {
    const params = new URLSearchParams(window.location.search);
    const [activeTab, setActiveTab] = useState("personal-information");
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const [isLoadingCompanyInfo, setLoadingCompanyInfo] = useState<boolean>(true);
    const [companyInfo, setCompanyInfo] = useState<Company | undefined>(undefined);

    // Company Information and Email Communication are recruiter-only tabs. Trainers
    // (role "training-agent"), Operators (role "agent") and Oura don't have them.
    const isTrainer = userInfo?.role === "training-agent";
    const isOperator = userInfo?.role === "agent";
    // Oura: real role in prod, or the ?product=oura hint in dev/staging.
    // Shared detector — in production only the JWT role decides; the URL
    // hint is honored outside production for previews.
    const { isOuraWs: isOura } = useOuraWorkspace();
    const hideRecruiterOnlyTabs = isTrainer || isOperator || isOura;

    // Oura's Settings sits outside /oura, so it names its own tab like every Oura page does.
    useEffect(() => {
        if (!isOura) return;
        const before = document.title;
        document.title = "Settings · Oura";
        return () => { document.title = before; };
    }, [isOura]);

    // Trainers get their own Email Communication tab (per-user notification
    // opt-outs). It's hidden entirely for read-only members (binary permission),
    // and never shown in the Oura context (Oura has no email settings).
    const showTrainerEmailTab = isTrainer && userInfo?.permission !== "member" && !isOura;

    useEffect(() => {
        console.log("Fetching company info...", params.get("tab"));
        if (!isLoadingUserProfile && userInfo && userInfo.role === "hr" && (params.get("tab") === "company-information" || params.get("tab") === "email-communication")) {
            getCompanyInfo(userInfo.company_id).then((resp) => {
                if (resp.status === 200) {
                    setCompanyInfo(resp.data.data);
                }
            }).finally(() => {
                setLoadingCompanyInfo(false);
            })
        }
    }, [isLoadingUserProfile, userInfo, params.get("tab")]);

    useEffect(() => {
        const tab = params.get("tab");
        if (tab && userInfo) {
            // Tabs that don't exist for this user — redirect those to the
            // default. Trainers keep their own Email Communication tab, so it's
            // only treated as hidden when the trainer email tab isn't shown.
            const hiddenTabs = [
                ...(hideRecruiterOnlyTabs ? ["company-information"] : []),
                ...(hideRecruiterOnlyTabs && !showTrainerEmailTab ? ["email-communication"] : []),
            ];
            if (hiddenTabs.includes(tab)) {
                setActiveTab("personal-information");
            } else {
                setActiveTab(tab);
            }
        }
    }, [params.get("tab"), userInfo])

    const onChangeTab = (tab: string) => {
        setActiveTab(tab);
        const url = new URL(window.location.href);
        url.searchParams.set("tab", tab);
        window.history.pushState({}, "", url.toString());
    }

    // #3573: a maintenance window closes every settings panel — avatar, SSO,
    // team invites, reviewers, companies/departments, branding, email
    // preferences and templates. Gating the panels rather than each control
    // means a panel added later is covered on arrival, and it is the honest
    // shape of the rule: nothing here can be changed right now.
    //
    // Deliberately NOT on the TabsList. Browsing your own settings is reading,
    // and reading is what should keep working during a window — locking the
    // tabs would strand whoever opened the page on whichever tab they landed
    // on, for no gain.
    const maintenance = useMaintenanceGate();

    return (
        <div className="w-full">
            {isOura ? (
                // Oura's own head: its type, British spelling, and only what Oura has here.
                <PageHead title="Settings" sentence="Your details, and who else can sign in." />
            ) : (
                <div className="mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-black">Settings</h1>
                        <p className="text-gray-600">
                            Keep your profile organized, manage your team’s role access, and update company details.
                        </p>
                    </div>
                </div>
            )}
            <Tabs value={activeTab} onValueChange={onChangeTab} className="w-full rounded-none">
                <TabsList className="flex w-full mb-8 bg-transparent h-auto p-0 border-b border-gray-200 justify-start rounded-none overflow-x-auto scrollbar-hide">
                    <TabsTrigger
                        value="personal-information"
                        className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
                        style={{
                            paddingTop: "8px",
                            paddingRight: "24px",
                            paddingBottom: "12px",
                            paddingLeft: "24px",
                        }}
                    >
                        <UserIcon className="h-4 w-4" />
                        {isOura ? "Your details" : "Personal Information"}
                    </TabsTrigger>
                    {!["admin", "super_admin", "job_seeker"].includes(userInfo?.role || "") && (
                        <>
                            <TabsTrigger
                                value="user-permissions"
                                className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
                                style={{
                                    paddingTop: "8px",
                                    paddingRight: "24px",
                                    paddingBottom: "12px",
                                    paddingLeft: "24px",
                                }}
                            >
                                <UsersIcon className="h-4 w-4" />
                                {isOura ? "Your team" : "Manage Team Access"}
                            </TabsTrigger>
                            {!hideRecruiterOnlyTabs && (
                                <TabsTrigger
                                    value="company-information"
                                    className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
                                    style={{
                                        paddingTop: "8px",
                                        paddingRight: "24px",
                                        paddingBottom: "12px",
                                        paddingLeft: "24px",
                                    }}
                                >
                                    <BuildingIcon className="h-4 w-4" />
                                    Company Information
                                </TabsTrigger>
                            )}
                        </>
                    )}
                    {!["admin", "super_admin", "job_seeker"].includes(userInfo?.role || "") && !hideRecruiterOnlyTabs && (
                        <TabsTrigger
                            value="email-communication"
                            className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
                            style={{
                                paddingTop: "8px",
                                paddingRight: "24px",
                                paddingBottom: "12px",
                                paddingLeft: "24px",
                            }}
                        >
                            <MailIcon className="h-4 w-4" />
                            Email Communication
                        </TabsTrigger>
                    )}
                    {showTrainerEmailTab && (
                        <TabsTrigger
                            value="email-communication"
                            className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
                            style={{
                                paddingTop: "8px",
                                paddingRight: "24px",
                                paddingBottom: "12px",
                                paddingLeft: "24px",
                            }}
                        >
                            <MailIcon className="h-4 w-4" />
                            Email Communication
                        </TabsTrigger>
                    )}
                </TabsList>

                <TabsContent value="personal-information" className="mt-0">
                    <div
                        className={isOura ? cn(SURFACE.card, "p-6", maintenance.groupClass) : `bg-white rounded-lg p-6 ${maintenance.groupClass}`}
                        {...maintenance.groupProps}
                        style={isOura ? undefined : {
                            paddingTop: "16px",
                            paddingRight: "16px",
                            paddingBottom: "16px",
                            paddingLeft: "16px",
                        }}
                    >
                        <SettingProfileFormV2 />
                    </div>
                </TabsContent>
                {!["admin", "super_admin"].includes(userInfo?.role || "") && (
                    <>
                        <TabsContent value="user-permissions" className="mt-0">
                            <div
                                className={isOura ? cn(SURFACE.card, "p-6", maintenance.groupClass) : `bg-white rounded-lg p-6 ${maintenance.groupClass}`}
                                {...maintenance.groupProps}
                                style={isOura ? undefined : {
                                    paddingTop: "16px",
                                    paddingRight: "16px",
                                    paddingBottom: "16px",
                                    paddingLeft: "16px",
                                }}
                            >
                                <SettingUserPermissions />
                            </div>
                        </TabsContent>

                        {!hideRecruiterOnlyTabs && (
                            <TabsContent value="company-information" className="mt-0">
                                <div
                                    className={`bg-white rounded-lg p-6 ${maintenance.groupClass}`}
                                    {...maintenance.groupProps}
                                    style={{
                                        paddingTop: "16px",
                                        paddingRight: "16px",
                                        paddingBottom: "16px",
                                        paddingLeft: "16px",
                                    }}
                                >
                                    {!isLoadingCompanyInfo && companyInfo && (
                                        <CompanyInformationSectionV2 company={companyInfo} />
                                    )}
                                </div>
                            </TabsContent>
                        )}
                    </>
                )}
                {!hideRecruiterOnlyTabs && (
                    <TabsContent
                        value="email-communication"
                        className="mt-0 flex flex-col"
                    >
                        <div className={`bg-white rounded-lg flex flex-col ${maintenance.groupClass}`} {...maintenance.groupProps}>
                            {!isLoadingCompanyInfo && companyInfo && (
                                <EmailCommunicationSection company={companyInfo} />
                            )}
                        </div>
                    </TabsContent>
                )}
                {showTrainerEmailTab && (
                    <TabsContent
                        value="email-communication"
                        className="mt-0 flex flex-col"
                    >
                        <div className={`bg-white rounded-lg flex flex-col ${maintenance.groupClass}`} {...maintenance.groupProps}>
                            <EmailCommunicationTrainerSection />
                        </div>
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}